import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreateMembershipPlanRequest,
  CreateMembershipPlanRequestSchema,
  DEFAULT_CURRENCY,
  UpdateMembershipPlanRequestSchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Types ──────────────────────────────────────────────────────────────────
//
// Values flowing through react-hook-form use DOLLARS for priceCents (display
// unit); conversion to integer cents happens at submit time via setValueAs,
// same boundary pattern as TariffForm.

export interface MembershipPlanFormValues {
  name: string;
  interval: 'week' | 'month' | 'quarter' | 'year';
  intervalCount: number;
  priceCents: number;
  currency: string;
  graceDays: number;
  maxRetries: number;
  retryIntervalHours: number;
}

interface MembershipPlanFormProps {
  mode: 'create' | 'edit';
  onSubmit: (values: CreateMembershipPlanRequest) => Promise<void>;
  defaultValues?: Partial<MembershipPlanFormValues>;
  submitLabel?: string;
  onCancel?: () => void;
}

// ── Dollar ↔ Cent / int helpers ─────────────────────────────────────────────

function dollarsToCents(v: string | number | null | undefined): number {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : Math.round(n * 100);
}

function toInt(v: string | number | null | undefined): number {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : Math.round(n);
}

// ── Component ──────────────────────────────────────────────────────────────

export function MembershipPlanForm({
  mode,
  onSubmit,
  defaultValues,
  submitLabel,
  onCancel,
}: MembershipPlanFormProps) {
  const { t } = useTranslation('memberships');
  const schema =
    mode === 'create' ? CreateMembershipPlanRequestSchema : UpdateMembershipPlanRequestSchema;
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MembershipPlanFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      interval: 'month',
      intervalCount: 1,
      priceCents: 0,
      currency: DEFAULT_CURRENCY,
      graceDays: 0,
      maxRetries: 3,
      retryIntervalHours: 24,
      ...defaultValues,
    },
  });

  async function handleFormSubmit(values: MembershipPlanFormValues) {
    setServerError(null);
    try {
      await onSubmit(values as unknown as CreateMembershipPlanRequest);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('membershipPlanForm.genericError') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      <div className="grid gap-6 md:grid-cols-2">
        <Field
          label={t('membershipPlanForm.planName.label')}
          htmlFor="name"
          error={errors.name?.message}
        >
          <Input
            id="name"
            placeholder={t('membershipPlanForm.planName.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
          />
        </Field>

        <Field
          label={t('membershipPlanForm.currency.label')}
          htmlFor="currency"
          error={errors.currency?.message}
        >
          <Input
            id="currency"
            placeholder={t('membershipPlanForm.currency.placeholder')}
            maxLength={3}
            disabled={isSubmitting}
            {...register('currency', { setValueAs: (v: string) => v.toUpperCase() })}
          />
        </Field>

        <Field
          label={t('membershipPlanForm.billingInterval')}
          htmlFor="interval"
          error={errors.interval?.message}
        >
          <select
            id="interval"
            disabled={isSubmitting}
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            {...register('interval')}
          >
            <option value="week">{t('membershipPlanForm.intervalOptions.week')}</option>
            <option value="month">{t('membershipPlanForm.intervalOptions.month')}</option>
            <option value="quarter">{t('membershipPlanForm.intervalOptions.quarter')}</option>
            <option value="year">{t('membershipPlanForm.intervalOptions.year')}</option>
          </select>
        </Field>

        <Field
          label={t('membershipPlanForm.intervalCount')}
          htmlFor="intervalCount"
          error={errors.intervalCount?.message}
        >
          <Input
            id="intervalCount"
            type="number"
            min={1}
            disabled={isSubmitting}
            {...register('intervalCount', { setValueAs: toInt })}
          />
        </Field>

        <Field
          label={t('membershipPlanForm.price.label')}
          htmlFor="priceCents"
          error={errors.priceCents?.message}
        >
          <Input
            id="priceCents"
            type="number"
            min={0}
            step="0.01"
            placeholder={t('membershipPlanForm.price.placeholder')}
            disabled={isSubmitting}
            {...register('priceCents', { setValueAs: dollarsToCents })}
          />
        </Field>

        <Field
          label={t('membershipPlanForm.graceDays')}
          htmlFor="graceDays"
          error={errors.graceDays?.message}
        >
          <Input
            id="graceDays"
            type="number"
            min={0}
            disabled={isSubmitting}
            {...register('graceDays', { setValueAs: toInt })}
          />
        </Field>

        <Field
          label={t('membershipPlanForm.maxRetries')}
          htmlFor="maxRetries"
          error={errors.maxRetries?.message}
        >
          <Input
            id="maxRetries"
            type="number"
            min={0}
            disabled={isSubmitting}
            {...register('maxRetries', { setValueAs: toInt })}
          />
        </Field>

        <Field
          label={t('membershipPlanForm.retryIntervalHours')}
          htmlFor="retryIntervalHours"
          error={errors.retryIntervalHours?.message}
        >
          <Input
            id="retryIntervalHours"
            type="number"
            min={1}
            disabled={isSubmitting}
            {...register('retryIntervalHours', { setValueAs: toInt })}
          />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting
            ? t('membershipPlanForm.saving')
            : (submitLabel ?? t('membershipPlanForm.savePlan'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
